'use client';

import { MouseEvent, useEffect, useState } from 'react';
import { getDelegateAccountLabel } from 'src/utils/account';
import { MarginMode, UserStatus } from '@drift-labs/sdk';
import MarginUpdateRow from './MarginUpdateRow';
import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { COMMON_UI_UTILS, matchEnum } from '@drift/common';
import useHighLeverageSpotsOpen from 'src/hooks/useHighLeverageSpotsOpen';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useModal from 'src/components/Modals/hooks/useModal';
import TextButton from 'src/components/primitives/TextButton/TextButton';

// Current subaccount on-chain state
type SubAccountInfo = {
	accountId: number;
	accountName: string;
	isDelegate?: boolean;
	maxLeverage: number;
	marginMode: MarginMode;
	marginTradingEnabled: boolean;
	reduceOnly: boolean;
	pmm: boolean;
};

// Updates for subaccount
export type SubAccountSettingsUpdateEntry = {
	subAccountId?: number;
	maxLeverage?: number; // Displayed as "Max Leverage"
	marginMode?: MarginMode; // High leverage mode enabled/disabled
	marginTradingEnabled?: boolean; // Spot margin enabled
	reduceOnly?: boolean;
	pmm?: boolean;
};

// All subaccount updates keyed by subaccount id
export type AllSubaccountUpdates = Record<
	number,
	SubAccountSettingsUpdateEntry
>;

const MarginSettings = (props: {
	allSubAccountUpdates: Record<number, SubAccountSettingsUpdateEntry>;
	addToMarginRatioUpdates: (
		subaccountId: number,
		newValue: number | undefined
	) => void;
	addToMarginTradingUpdates: (subaccountId: number, newValue: boolean) => void;
	addToReduceOnlyUpdates: (subaccountId: number, newValue: boolean) => void;
	addToMarginModeUpdates: (
		subaccountId: number,
		newValue: MarginMode | -1
	) => void;
	addToPmmUpdates: (subaccountId: number, newValue: boolean) => void;
}) => {
	const { highLeverageSpotsOpen, updateHighLeverageSlotsOpen } =
		useHighLeverageSpotsOpen();
	const connected = useWalletIsConnected();
	const subAccounts = useDriftAccountStore((s) => s.accounts) as Record<
		string,
		AccountData
	>;
	const [subAccountInfos, setSubAccountInfos] = useState<SubAccountInfo[]>([]);
	const { push } = useModal();

	const fetchSubAccountInfos = () => {
		setSubAccountInfos(
			Object.values(subAccounts)
				.map((acct) => {
					if (!acct.client || !acct.client?.isSubscribed) return;

					const userAccount = acct.client.getUserAccount();

					const customMarginRatio = userAccount.maxMarginRatio;

					const maxLeverage =
						COMMON_UI_UTILS.convertMarginRatioToLeverage(customMarginRatio);

					return {
						accountId: acct.userId,
						accountName: acct.isDelegatedTo
							? getDelegateAccountLabel(acct)
							: acct.name,
						isDelegate: acct.isDelegatedTo ?? false,
						maxLeverage,
						marginMode: userAccount.marginMode,
						marginTradingEnabled: userAccount.isMarginTradingEnabled,
						reduceOnly: acct.client.hasStatus(UserStatus.REDUCE_ONLY),
						pmm: acct.client.hasStatus(UserStatus.PROTECTED_MAKER),
					};
				})
				.filter(Boolean) as SubAccountInfo[]
		);
	};

	const setMaxLeverage = (accountId: number, newValue: number | undefined) => {
		const subaccountInfo = subAccountInfos.find(
			(info) => info.accountId === accountId
		);
		// Kick users out of high leverage mode if they set a max leverage
		if (
			(newValue ?? 0) > 0 &&
			(matchEnum(subaccountInfo?.marginMode, MarginMode.HIGH_LEVERAGE) ||
				matchEnum(
					props.allSubAccountUpdates[accountId]?.marginMode,
					MarginMode.HIGH_LEVERAGE
				))
		) {
			props.addToMarginModeUpdates(accountId, MarginMode.DEFAULT);
		}
		props.addToMarginRatioUpdates(accountId, newValue);
	};

	const setMarginMode = (accountId: number, newValue: MarginMode) => {
		const subaccountInfo = subAccountInfos.find(
			(info) => info.accountId === accountId
		);

		// If users select high leverage mode, remove their max leverage setting
		if (
			matchEnum(newValue, MarginMode.HIGH_LEVERAGE) &&
			((subaccountInfo?.maxLeverage ?? 0) > 0 ||
				(props.allSubAccountUpdates[accountId]?.maxLeverage ?? 0) > 0)
		) {
			props.addToMarginRatioUpdates(accountId, undefined);
		}

		const checkIsNormalMode = (marginMode: MarginMode) => {
			return (
				matchEnum(marginMode, MarginMode.DEFAULT) ||
				matchEnum(marginMode, MarginMode.HIGH_LEVERAGE_MAINTENANCE)
			);
		};

		// Pass -1 if the new value matches the original saved value, otherwise pass the new value
		const isNewValueNormalMode = checkIsNormalMode(newValue);
		const isOriginalMarginModeNormalMode = checkIsNormalMode(
			subaccountInfo?.marginMode ?? MarginMode.DEFAULT
		);
		const isRevertingToOriginal =
			isNewValueNormalMode === isOriginalMarginModeNormalMode;
		const valueToPass = isRevertingToOriginal ? -1 : newValue;
		props.addToMarginModeUpdates(accountId, valueToPass);
	};

	const setMarginEnabled = (accountId: number, newValue: boolean) => {
		props.addToMarginTradingUpdates(accountId, newValue);
	};

	const setReduceOnly = (accountId: number, newValue: boolean) => {
		props.addToReduceOnlyUpdates(accountId, newValue);
	};

	const setPmm = (accountId: number, newValue: boolean) => {
		props.addToPmmUpdates(accountId, newValue);
	};

	const handleAddNewSubaccount = (event: MouseEvent) => {
		event.preventDefault();
		push({ type: 'new_subaccount', props: {} });
	};

	useEffect(() => {
		if (connected) {
			fetchSubAccountInfos();
			updateHighLeverageSlotsOpen();
		}
	}, [connected, subAccounts, updateHighLeverageSlotsOpen]);

	return (
		<div className="flex flex-col gap-y-lg">
			<p className="typo-b4 text-text-secondary">
				The maximum leverage applies to your subaccount&apos;s total leverage,
				not your position. To open an isolated position, you can create a new
				subaccount instead{' '}
				<TextButton
					size="sm"
					variant="tertiary"
					onClick={handleAddNewSubaccount}
				>
					here
				</TextButton>
				.
			</p>
			<p className="typo-b4 text-text-secondary">
				Higher leverage (up to 101x) is available to a limited number of
				accounts. Spots remaining:{' '}
				<span className="text-star-yellow">{highLeverageSpotsOpen}</span>
			</p>
			<div className="flex flex-col gap-y-lg">
				{subAccountInfos
					.filter((subAcct) => !subAcct.isDelegate)
					.map((subAcct) => {
						const highLeverageEnabled = matchEnum(
							props.allSubAccountUpdates[subAcct.accountId]?.marginMode ||
								subAcct.marginMode ||
								MarginMode.DEFAULT,
							MarginMode.HIGH_LEVERAGE
						);

						const hasMaxLeverageSettingUpdate =
							props.allSubAccountUpdates[subAcct.accountId] &&
							Object.keys(
								props.allSubAccountUpdates[subAcct.accountId]
							).includes('maxLeverage');

						const currentMaxLev = hasMaxLeverageSettingUpdate
							? props.allSubAccountUpdates[subAcct.accountId]?.maxLeverage
							: subAcct.maxLeverage;

						return (
							<MarginUpdateRow
								key={`${subAcct.accountName}_margin`}
								label={subAcct.accountName}
								highLeverageEnabled={highLeverageEnabled}
								currentMaxLev={currentMaxLev}
								marginEnabledChecked={subAcct.marginTradingEnabled}
								reduceOnly={subAcct.reduceOnly}
								onChangeEnabled={(newValue: boolean) => {
									setMarginEnabled(subAcct.accountId, newValue);
								}}
								onChangeRatio={(newValue: number | undefined) => {
									const valueToPass =
										// `-1` indicates that the form value is the same as the saved value.
										// See `addToMarginRatioUpdates`.
										newValue === subAcct.maxLeverage ? -1 : newValue;

									setMaxLeverage(subAcct.accountId, valueToPass);
								}}
								onChangeReduceOnly={(newValue: boolean) => {
									setReduceOnly(subAcct.accountId, newValue);
								}}
								onChangeMarginMode={(newValue: MarginMode) => {
									setMarginMode(subAcct.accountId, newValue);
								}}
								pmm={subAcct.pmm}
								onChangePmm={(newValue: boolean) => {
									setPmm(subAcct.accountId, newValue);
								}}
								disabled={false}
							/>
						);
					})}
				{subAccountInfos.filter((subAcct) => subAcct.isDelegate)?.length >
					0 && (
					<span className="typo-t3 text-text-default">Delegated accounts</span>
				)}
				{subAccountInfos
					.filter((subAcct) => subAcct.isDelegate)
					.map((subAcct) => {
						const highLeverageEnabled = matchEnum(
							props.allSubAccountUpdates[subAcct.accountId]?.marginMode ||
								subAcct.marginMode ||
								MarginMode.DEFAULT,
							MarginMode.HIGH_LEVERAGE
						);

						const hasMaxLeverageSettingUpdate =
							props.allSubAccountUpdates[subAcct.accountId] &&
							Object.keys(
								props.allSubAccountUpdates[subAcct.accountId]
							).includes('maxLeverage');

						const currentMaxLev = hasMaxLeverageSettingUpdate
							? props.allSubAccountUpdates[subAcct.accountId]?.maxLeverage
							: subAcct.maxLeverage;

						return (
							<MarginUpdateRow
								key={`${subAcct.accountName}_margin`}
								label={subAcct.accountName}
								highLeverageEnabled={highLeverageEnabled}
								currentMaxLev={currentMaxLev}
								marginEnabledChecked={subAcct.marginTradingEnabled}
								reduceOnly={subAcct.reduceOnly}
								pmm={subAcct.pmm}
								onChangeEnabled={(_newValue: boolean) => {
									// disabled
								}}
								onChangeRatio={(_newValue: number | undefined) => {
									// disabled
								}}
								onChangeReduceOnly={(_newValue: boolean) => {
									// disabled
								}}
								onChangeMarginMode={(_newValue: MarginMode) => {
									// disabled
								}}
								onChangePmm={(_newValue: boolean) => {
									// disabled
								}}
								disabled={true}
							/>
						);
					})}
			</div>
		</div>
	);
};

export default MarginSettings;
